<template>
  <div class="automatic-emails-section" :class="{ 'is-bare': bare }">
    <div v-if="!bare" class="history-section-header" @click="toggleCollapse">
      <h3>
        <i class="fas fa-envelope"></i> {{ title }}
        <span class="section-count">({{ emailsCount }})</span>
        <InfoButton 
          v-if="showInfoButton"
          @click.stop="$emit('show-info')"
          title="En savoir plus sur les mails automatiques"
        />
      </h3>
      <i 
        :class="['section-chevron', { 'expanded': !collapsed }]"
        class="fas fa-chevron-down"
      ></i>
    </div>
    <div
      class="history-section-content"
      :class="{ 'collapsed': collapsed && !bare }"
    >
      <div v-if="emails && emails.length > 0" class="history-list">
        <!-- Même anatomie qu'une ligne d'appel (CallItem) : pastille ronde,
             titre + date, contenu, puis méta avec l'action à droite. -->
        <div v-for="email in paginatedEmails" :key="email.id" class="email-item">
          <span class="email-avatar" aria-hidden="true">
            <i class="fas fa-envelope"></i>
          </span>

          <div class="email-body">
            <div class="email-line">
              <span class="email-title">{{ getEmailTypeLabel(email.email_type) }}</span>
              <span class="email-date">{{ formatDateTime(email.sent_at) }}</span>
            </div>

            <p v-if="email.subject" class="email-subject">{{ email.subject }}</p>

            <div class="email-meta">
              <span v-if="showRecipient && email.recipient_email">{{ email.recipient_email }}</span>
              <router-link
                v-if="email.quote_human_id && showQuoteLink"
                :to="`/quotes/${email.quote_id}`"
                @click.stop
              >
                Demande #{{ email.quote_human_id }}
              </router-link>
              <span v-if="showQuoteLink && email.quote_formation_nom">{{ email.quote_formation_nom }}</span>
              <router-link
                v-if="email.inscription_id && showInscriptionLink"
                :to="`/inscriptions/${email.inscription_id}`"
                @click.stop
              >
                {{ email.inscription_label || `#${email.inscription_id}` }}
              </router-link>

              <span v-if="getPreviewTemplateId(email.email_type)" class="email-actions">
                <router-link
                  :to="getPreviewLink(email)"
                  target="_blank"
                  class="row-action"
                >
                  Aperçu
                </router-link>
              </span>
            </div>
          </div>
        </div>
        <Pagination
          :current-page="currentPage"
          :total="emails.length"
          :items-per-page="PAGE_SIZE"
          :show-info="false"
          @update:current-page="currentPage = $event"
        />
      </div>
      <DetailEmpty v-else tag="p" class="no-history" :text="emptyMessage" />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { InfoButton, Pagination } from '../ui'
import DetailEmpty from '../data/DetailEmpty.vue'
import { formatDateTime } from '../../utils/dateFormatters.js'
import { getEmailTypeLabel } from '../../utils/formatting.js'
import { useEmailPreviewLink } from '../../composables/email/useEmailPreviewLink.js'

const props = defineProps({
  /** Rend la liste sans en-tête ni cadre : le parent porte déjà le titre. */
  bare: {
    type: Boolean,
    default: false
  },
  emails: {
    type: Array,
    default: () => []
  },
  title: {
    type: String,
    default: 'Mails automatiques envoyés'
  },
  emptyMessage: {
    type: String,
    default: 'Aucun mail automatique envoyé.'
  },
  showInfoButton: {
    type: Boolean,
    default: true
  },
  showRecipient: {
    type: Boolean,
    default: true
  },
  showQuoteLink: {
    type: Boolean,
    default: false
  },
  showInscriptionLink: {
    type: Boolean,
    default: false
  },
  storageKey: {
    type: String,
    default: 'automatic-emails'
  },
  context: {
    type: String,
    default: 'quote'
  }
})

defineEmits(['show-info'])

const collapsed = ref(false)
const currentPage = ref(1)
const PAGE_SIZE = 10

const paginatedEmails = computed(() => {
  if (!props.emails) return []
  const start = (currentPage.value - 1) * PAGE_SIZE
  return props.emails.slice(start, start + PAGE_SIZE)
})

// Reset page when emails list changes
watch(() => props.emails?.length, () => {
  currentPage.value = 1
})

// Clé de stockage unique
const getStorageKey = () => {
  return `${props.context}-history-section-${props.storageKey}`
}

// Charger l'état depuis localStorage
const loadState = () => {
  try {
    const key = getStorageKey()
    const saved = localStorage.getItem(key)
    if (saved !== null) {
      collapsed.value = JSON.parse(saved)
    }
  } catch (err) {
    console.warn(`Erreur lors du chargement de l'état de la section ${props.storageKey}:`, err)
  }
}

// Sauvegarder l'état dans localStorage
const saveState = () => {
  try {
    const key = getStorageKey()
    localStorage.setItem(key, JSON.stringify(collapsed.value))
  } catch (err) {
    console.warn(`Erreur lors de la sauvegarde de l'état de la section ${props.storageKey}:`, err)
  }
}

// Fonction de toggle
const toggleCollapse = () => {
  collapsed.value = !collapsed.value
  saveState()
}

// Compteur d'emails
const emailsCount = computed(() => {
  return props.emails?.length || 0
})

// Le lien n'apparaît que sur un modèle que le rôle a le droit d'ouvrir : un
// commercial ne voit que ceux atteignables depuis ses fiches.
const { previewTemplateId: getPreviewTemplateId, previewLink: getPreviewLink } = useEmailPreviewLink()

// Charger l'état au montage
onMounted(() => {
  loadState()
})
</script>

<style scoped>
.automatic-emails-section {
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
  padding: var(--spacing-md);
  background: var(--color-bg-secondary);
}

.automatic-emails-section.is-bare {
  border: none;
  border-radius: 0;
  padding: 0;
  background: none;
}

.history-section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: var(--spacing-md);
  cursor: pointer;
  user-select: none;
  padding: var(--spacing-sm);
  border-radius: var(--radius-sm);
  transition: background-color var(--transition-base);
}

.history-section-header:hover {
  background-color: var(--color-bg-primary);
}

.history-section-header h3 {
  margin: 0;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  flex: 1;
  color: var(--color-text-primary);
}

.section-count {
  color: var(--color-text-tertiary);
  font-weight: 500;
  font-size: 0.9rem;
}

.section-chevron {
  color: var(--color-text-tertiary);
  font-size: 0.9rem;
  transition: transform var(--transition-base);
  flex-shrink: 0;
  cursor: pointer;
  padding: var(--spacing-xs);
  border-radius: var(--radius-xs);
  transition: transform var(--transition-base), background-color var(--transition-base);
}

.section-chevron:hover {
  background-color: var(--color-bg-primary);
}

.section-chevron.expanded {
  transform: rotate(180deg);
}

.history-section-content {
  max-height: 5000px;
  overflow: visible;
  transition: max-height var(--transition-slow), opacity var(--transition-base);
  opacity: 1;
}

.history-section-content.collapsed {
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  margin-top: 0;
}

.history-list {
  display: flex;
  flex-direction: column;
}

/* Une ligne de mail se lit comme une ligne d'appel : mêmes cotes que
   .call-item (CallItem.vue), seule l'icône de la pastille change. */
.email-item {
  display: flex;
  gap: var(--spacing-4);
  padding: var(--spacing-3) 0;
  border-bottom: 1px solid var(--color-border-light);
}

.email-item:last-of-type {
  border-bottom: 0;
}

.email-avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: var(--radius-full);
  background: var(--color-bg-tertiary);
  color: var(--color-text-tertiary);
  font-size: 0.8rem;
  line-height: 1;
  flex-shrink: 0;
}

.email-body {
  flex: 1;
  min-width: 0;
}

.email-line {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--spacing-3);
}

/* Le titre cède la place à la date, jamais l'inverse : sans min-width, un
   libellé d'un seul tenant (un type brut non traduit) poussait la date hors
   de la carte au lieu de passer à la ligne. */
.email-title {
  min-width: 0;
  overflow-wrap: anywhere;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.email-date {
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
  flex-shrink: 0;
}

.email-subject {
  margin: 3px 0 0;
  font-size: var(--font-size-sm);
  color: var(--color-text-secondary);
  line-height: var(--line-height-normal);
}

.email-meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: var(--spacing-1) var(--spacing-3);
  margin-top: var(--spacing-1);
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
}

.email-actions {
  display: flex;
  gap: var(--spacing-1);
  margin-left: auto;
}

/* row-action est pensé pour un <button> : sur un lien, on neutralise le
   soulignement et on centre le libellé dans la cible cliquable. */
.email-actions .row-action {
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

/* La phrase d'absence de toutes les fiches (`DetailEmpty`) ; seul l'espace
   autour est propre à la liste. */
.history-section-content .no-history {
  padding: var(--spacing-4) 0;
}

@media (max-width: 768px) {
  .email-line {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
  }
}
</style>

